import { Col, Form, Row, Table, Input, Button, Tooltip } from 'antd';
import { t } from 'i18next';
import { ChangeEventHandler, FC, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useUIDispatchContext } from 'providers/UI';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { deleteRole, fetchRoles } from 'services/api/roles';
import { RoleListRow } from 'types/api/roles';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import { roleEditRefusal } from 'utils/ceiling';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';

const Roles: FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const timer = useRef<NodeJS.Timeout | null>(null);
  const { toggleModal } = useUIDispatchContext();

  const [rows, setRows] = useState<RoleListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchRoles();
        if (!cancelled) setRows(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const results = rows.filter(item =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    []
  );

  const onDeleteRole = useCallback(
    (id: number) => {
      toggleModal('confirm', true, {
        async onConfirm() {
          try {
            await deleteRole(id);
            refetch();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [refetch, toggleModal]
  );

  return (
    <PageWrapper
      title="Roles"
      color="white"
      extra={[
        <Private
          key="create-role"
          route={PERMISSION_ROUTES.roles}
          action={PERMISSION_ACTIONS.create}
        >
          <Button type="primary" onClick={() => navigate('details')}>
            <PlusOutlined />
            Create role
          </Button>
        </Private>
      ]}
    >
      <Row gutter={[16, 0]}>
        <Col sm={6}>
          <Form.Item>
            <Input.Search
              placeholder={t('common.search')}
              onChange={onSearchChange}
            />
          </Form.Item>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            // The display name. `name` below is the identifier the backend
            // matches on, so both are worth showing.
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            render: (label, record) => label || record.name
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
          },
          {
            // Server-computed, one grouped count over core.user_roles for the
            // whole page. Counting the admins list instead would under-report
            // for every operator but a superadmin, whose view of that list is
            // the only unscoped one. Absent on an older API, hence the dash.
            title: 'Users',
            dataIndex: 'holderCount',
            key: 'holderCount',
            width: '10%',
            render: (holderCount?: number) =>
              typeof holderCount === 'number' ? holderCount : '—'
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value: number) => dayjs(value).format(FULL_DATE_FORMAT)
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'x',
            width: '5%',
            align: 'right',
            render: (id, record) => {
              // Role writes are no longer super_admin-only: every admin may
              // write a role whose permissions are a subset of their own. So
              // "can this operator act on THIS role" is a per-row question —
              // and the row answers it. `canAct` is decided by the same object
              // the write routes enforce, and `canActReason` is its sentence,
              // which names the sections that exceed the ceiling where our own
              // wording could only say that some did.
              const blocked = roleEditRefusal(record);

              // Disabled with the reason attached, not hidden. A control that
              // vanishes teaches the operator nothing; the backend answers
              // these with a message and so should we.
              // The reason rides on the item's label: MenuItemProps omits
              // `title`, which antd reserves for its own tooltip.
              const label = (text: string) =>
                blocked ? <Tooltip title={blocked}>{text}</Tooltip> : text;

              return (
                <ActionMenu
                  data={[
                    {
                      title: label(String(t('table.edit'))),
                      permission: {
                        route: PERMISSION_ROUTES.roles,
                        action: PERMISSION_ACTIONS.update
                      },
                      action() {
                        if (blocked) return;
                        navigate(`details/${id}`);
                      },
                      menuProps: { disabled: !!blocked }
                    },
                    {
                      title: label(String(t('table.delete'))),
                      permission: {
                        route: PERMISSION_ROUTES.roles,
                        action: PERMISSION_ACTIONS.delete
                      },
                      action: () => {
                        if (blocked) return;
                        onDeleteRole(id);
                      },
                      menuProps: {
                        disabled: !!blocked,
                        style: {
                          color: blocked ? undefined : 'var(--danger-color)'
                        }
                      }
                    }
                  ]}
                  placement="bottomRight"
                  icon={<EllipsisOutlined />}
                />
              );
            }
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Roles;
